import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Cake } from 'lucide-react';
import { format } from 'date-fns';
import { es, enUS } from 'date-fns/locale';
import api from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import useIsMobile from '../../hooks/useIsMobile';

export const meta = {
  id: 'upcoming_birthdays',
  category: 'calendar',
  titleKey: 'dashboard.widgets.upcomingBirthdays',
  descriptionKey: 'dashboard.widgets.upcomingBirthdaysDesc',
  defaultSize: 'xl',
  icon: Cake,
};

export default function UpcomingBirthdaysWidget() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const locale = user?.language === 'en' ? enUS : es;

  const { data: upcomingBirthdays = [] } = useQuery({
    queryKey: ['dashboard-birthdays'],
    queryFn: () => api.get('/dashboard/upcoming-birthdays?days=30').then(r => r.data),
    refetchInterval: 60 * 60 * 1000,
  });

  if (upcomingBirthdays.length === 0) {
    return (
      <div className="card" style={{ height: '100%' }}>
        <div className="card-header">
          <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Cake size={16} />
            {t('dashboard.upcomingBirthdays')}
          </div>
        </div>
        <div style={{ padding: 16, textAlign: 'center', color: 'var(--c-text-muted)', fontSize: 13 }}>—</div>
      </div>
    );
  }

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden', height: '100%' }}>
      <div className="card-header" style={{ padding: '14px 20px', borderBottom: '1px solid var(--c-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Cake size={16} />
          {t('dashboard.upcomingBirthdays')}
        </div>
        <span style={{ fontSize: 11, color: 'var(--c-text-muted)' }}>{t('dashboard.next30Days')}</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${isMobile ? '260px' : '300px'}, 1fr))`, gap: 0 }}>
        {upcomingBirthdays.slice(0, 12).map((b) => {
          const isToday = b.daysUntil === 0;
          const isTomorrow = b.daysUntil === 1;
          const monthDate = new Date(2000, b.month - 1, b.day);
          const dayMonth = format(monthDate, 'd MMM', { locale });
          const color = '#ec4899';
          return (
            <div
              key={`${b.type}-${b.id}`}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 20px',
                borderBottom: '1px solid var(--c-border-subtle)',
                background: isToday ? 'rgba(236, 72, 153, 0.08)' : 'transparent',
              }}
            >
              <div style={{ width: 4, height: 28, borderRadius: 2, background: color, flexShrink: 0 }} />
              <div style={{ width: 52, flexShrink: 0, textAlign: 'center' }}>
                <div style={{ fontSize: 15, fontWeight: 700, lineHeight: 1.2, color: isToday ? color : 'inherit' }}>
                  {b.day}
                </div>
                <div style={{ fontSize: 10, color: 'var(--c-text-muted)', textTransform: 'uppercase', fontWeight: 600 }}>
                  {format(monthDate, 'MMM', { locale })}
                </div>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {b.name}
                </div>
                <div style={{ fontSize: 11, color: 'var(--c-text-muted)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {b.type === 'employee' ? t('dashboard.team') : (b.company_name || t('dashboard.contact'))}
                </div>
              </div>
              <span style={{
                fontSize: 10, padding: '2px 8px', borderRadius: 999,
                background: color + '18', color, fontWeight: 600, flexShrink: 0,
              }}>
                {isToday ? t('dashboard.today') : isTomorrow ? t('dashboard.tomorrow') : `${dayMonth}`}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
